import React, { useState, useEffect } from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, Button,
  List, ListItem, ListItemText, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogActions, Typography, Link, Chip
} from '@mui/material';
import axios from 'axios';
import api from '../utils/api';
import { sanitizedHtml } from '../utils/sanitize';

const RADIKO_SCHEDULE_URL = 'https://radiko.eystudi0.com/schedule';

function ManualRecording() {
  const [date, setDate] = useState('');
  const [channel, setChannel] = useState('');
  const [channelOptions, setChannelOptions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProgramDetail, setSelectedProgramDetail] = useState(null);

  useEffect(() => {
    document.title = '手動錄製';

    const todayDate = getTodayDate();
    setDate(todayDate);

    api.get('/channels')
      .then(res => {
        const options = (res.data || []).map(c => ({
          value: c.id,
          label: c.name,
          replay: !!c.has_replay,
        }));
        setChannelOptions(options);
        if (options.length > 0) {
          const first = options[0];
          setChannel(first.value);
          const dates = getDatesForChannel(first.replay);
          setAvailableDates(dates);
          fetchPrograms(first.value, todayDate, first);
        }
      })
      .catch(err => console.error('取得頻道列表失敗:', err));
  }, []);

  const getDatesForChannel = (isReplay = false) => {
    const dates = [];
    const now = new Date();
    const localOffset = now.getTimezoneOffset() * 60000;
    const jstOffset = 9 * 3600 * 1000;
    const jstTime = new Date(now.getTime() + localOffset + jstOffset);

    if (jstTime.getHours() < 5) {
      jstTime.setDate(jstTime.getDate() - 1);
    }

    const range = isReplay ? { from: -6, to: 7 } : { from: 0, to: 7 };
    for (let i = range.from; i < range.to; i++) {
      const d = new Date(jstTime);
      d.setDate(jstTime.getDate() + i);
      const formatted = `${d.getFullYear()}${(d.getMonth() + 1).toString().padStart(2, '0')}${d.getDate().toString().padStart(2, '0')}`;
      dates.push({
        label: `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`,
        value: formatted,
      });
    }
    return dates;
  };

  const getTodayDate = () => {
    const now = new Date();
    const localOffset = now.getTimezoneOffset() * 60000;
    const jstOffset = 9 * 3600 * 1000;
    const jstTime = new Date(now.getTime() + localOffset + jstOffset);

    if (jstTime.getHours() < 5) {
      jstTime.setDate(jstTime.getDate() - 1);
    }

    return `${jstTime.getFullYear()}${(jstTime.getMonth() + 1).toString().padStart(2, '0')}${jstTime.getDate().toString().padStart(2, '0')}`;
  };

  const formatProgramTime = (timeString) => {
    if (!timeString) return '時間未知';
    const year = timeString.substring(0, 4);
    const month = timeString.substring(4, 6);
    const day = timeString.substring(6, 8);
    const hour = timeString.substring(8, 10);
    const minute = timeString.substring(10, 12);
    const second = timeString.substring(12, 14);
    const d = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}+09:00`);
    return d.toLocaleString('zh-TW', { timeZone: 'Asia/Tokyo' });
  };

  const handleDateChange = (event) => {
    setDate(event.target.value);
    fetchPrograms(channel, event.target.value);
  };

  const handleChannelChange = (event) => {
    const selected = event.target.value;
    setChannel(selected);

    const data = channelOptions.find(c => c.value === selected);
    const isReplay = data?.replay || false;

    const dates = getDatesForChannel(isReplay);
    setAvailableDates(dates);

    // 盡量保留使用者已選的日期；新電台沒有該日期時才退回今天（再不行取第一個）
    const todayDate = getTodayDate();
    const pick = [date, todayDate].find(v => dates.some(d => d.value === v));
    const selectedDate = pick || dates[0].value;
    setDate(selectedDate);

    fetchPrograms(selected, selectedDate);
  };

  const handleProgramClick = (program) => {
    setSelectedProgramDetail(program);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const fetchPrograms = async (stationId, d, channelData = null) => {
    if (!stationId || !d) return;
    try {
      // Radiko schedule 是外部第三方服務，不走 /api/v2，獨立呼叫避免帶 Bearer。
      const [scheduleRes, scheduledRes] = await Promise.all([
        axios.get(`${RADIKO_SCHEDULE_URL}?channel=${stationId}&date=${d}`),
        api.get('/programs/scheduled-ids', { params: { station_id: stationId, date: d } }),
      ]);

      const now = new Date();
      const scheduledIds = scheduledRes.data?.scheduled || [];

      const selectedChannelData = channelData || channelOptions.find(c => c.value === stationId);
      const isReplay = selectedChannelData?.replay || false;

      const fetchedPrograms = scheduleRes.data.stations[0].programs.program
        .filter(program => {
          if (isReplay) return true;
          const endTime = new Date(
            program.to.replace(
              /(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/,
              '$1-$2-$3T$4:$5:$6+09:00'
            )
          );
          return endTime > now;
        })
        .map(program => {
          const id = `${stationId}${program.ft}`;
          return {
            ...program,
            id,
            scheduled: scheduledIds.includes(id),
          };
        });

      setPrograms(fetchedPrograms);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const handleProgramSelect = (program) => {
    const currentIndex = selectedPrograms.indexOf(program);
    const newSelected = [...selectedPrograms];
    if (currentIndex === -1) newSelected.push(program);
    else newSelected.splice(currentIndex, 1);
    setSelectedPrograms(newSelected);
  };

  const submitRecordings = async () => {
    if (selectedPrograms.length === 0) return;
    const failures = [];
    await Promise.all(selectedPrograms.map(async (program) => {
      try {
        // 後端只用 station_id 和 start_time，節目 metadata 由後端向 radiko 反查
        await api.post('/programs', {
          station_id: channel,
          start_time: program.ft,
        });
      } catch (err) {
        console.error('新增錄音失敗:', err);
        failures.push(program.title || program.id);
      }
    }));

    if (failures.length === 0) {
      alert('錄製任務已成功提交');
    } else {
      alert(`部分錄製任務提交失敗：\n${failures.join('\n')}`);
    }
    setSelectedPrograms([]);
    fetchPrograms(channel, date);
  };

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel>日期</InputLabel>
        <Select value={date} label="日期" onChange={handleDateChange}>
          {availableDates.map(d => (
            <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel>頻道</InputLabel>
        <Select value={channel} label="頻道" onChange={handleChannelChange}>
          {channelOptions.map(c => (
            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <List>
        {programs.map((program, index) => (
          <ListItem
            key={index}
            button
            onClick={() => handleProgramClick(program)}
            style={{ opacity: program.scheduled ? 0.5 : 1 }}
          >
            <ListItemText
              primary={program.title}
              secondary={`時間：${formatProgramTime(program.ft)} - ${formatProgramTime(program.to)}`}
            />
            <Checkbox
              edge="end"
              checked={selectedPrograms.includes(program)}
              disabled={program.scheduled}
              onClick={(event) => {
                event.stopPropagation();
                if (!program.scheduled) handleProgramSelect(program);
              }}
            />
          </ListItem>
        ))}
      </List>

      <Button variant="contained" color="primary" onClick={submitRecordings}>
        提交錄製任務
      </Button>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedProgramDetail?.title}</DialogTitle>
        <DialogContent>
          <Box>
            {selectedProgramDetail?.img && (
              <img src={selectedProgramDetail.img} alt="Program" style={{ maxWidth: '100%', marginBottom: '10px' }} />
            )}
            <Typography
              variant="body1"
              component="div"
              dangerouslySetInnerHTML={sanitizedHtml(selectedProgramDetail?.description || selectedProgramDetail?.desc || '')}
            />
            <Typography variant="body2" color="textSecondary">
              時間：{formatProgramTime(selectedProgramDetail?.ft)} - {formatProgramTime(selectedProgramDetail?.to)}
            </Typography>
            {Array.isArray(selectedProgramDetail?.person) && selectedProgramDetail.person.length > 0 && (
              <Typography variant="body2" color="textSecondary">
                演出者：{selectedProgramDetail.person.map(p => p.name).join(', ')}
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary">
              節目：{selectedProgramDetail?.season_name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              摘要：<span dangerouslySetInnerHTML={sanitizedHtml(selectedProgramDetail?.summary || '')} />
            </Typography>
            {selectedProgramDetail?.url && (
              <Link href={selectedProgramDetail.url} target="_blank" rel="noopener">
                更多訊息
              </Link>
            )}
            {Array.isArray(selectedProgramDetail?.tag) && selectedProgramDetail.tag.map((tag, i) => (
              <Chip key={typeof tag === 'string' ? tag : i} label={typeof tag === 'string' ? tag : tag.name || JSON.stringify(tag)} style={{ margin: '5px' }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ManualRecording;
